import { useContext, useState } from 'react';
import { Formik, Form, Field } from 'formik';
// eslint-disable-next-line import/order
import * as Yup from 'yup';
import 'yup-phone';

import {
  FormGroup,
  FormLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useSelector } from 'react-redux';

import { Input } from '@components/Form/Input';
import { LeadCollectionContext } from '@hooks/useLeadCollection';
import Button from '@common/components/Forms/components/Button';
import { PhoneNumberInput } from '@components/Form/PhoneNumberInput';
import { CountrySelect } from '@components/Form/CountrySelect';
import { getLanguagePrefix, getLocalizedContent as l } from '@helpers';
import content from '@components/Form/CustomerLead/LeadInputForm.json';
import { VariantImage } from '@components/Form/CustomerLead/VariantImage';
import CarVariants from '@components/Form/CustomerLead/CarVariants.json';
import VariantCheckbox from '@components/Form/VariantCheckbox/VariantCheckbox';
import Terms from '@common/components/Forms/components/Terms';
import { createSalesLead } from '@api/leadCollectionApi';
import { useAnalytics } from '@utils';
import colors from '@theme/colors';

const useStyles = makeStyles(() => ({
  formLabel: {
    fontWeight: '500',
    fontSize: '12px',
    lineHeight: '16px',
    color: '#000000',
  },
  title: {
    fontSize: '20px',
    lineHeight: '24px',
    color: colors['charcoal'],
    fontWeignt: 400,
    paddingTop: '30px',
    paddingBottom: '30px',
  },
}));

const LeadInputForm = ({ setIsComplete }) => {
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    campaignId,
    country,
    setCountry,
    interestedVariants,
    setInterestedVariants,
    setLeadCollectionResponse,
  } = useContext(LeadCollectionContext);
  const [phoneNumberCountryCode, setPhoneNumberCountryCode] = useState('nl');
  const [optInConfirmation, setOptInConfirmation] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const initialValues = {
    firstName: firstName,
    lastName: lastName,
    email: email,
    phoneNumber: phoneNumber,
    campaignId: campaignId,
    country: country,
    interestedVariants: interestedVariants,
    optInConfirmation: optInConfirmation,
  };
  const { trackProcess } = useAnalytics();

  const styles = useStyles();
  const { prefixLanguage: language } = useSelector(({ appState }) => appState);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const LeadCollectionSchema = Yup.object().shape({
    firstName: Yup.string()
      .matches(/^[aA-zZ\s]+$/, 'Only alphabets allowed')
      .nullable(false)
      .required('Required'),
    lastName: Yup.string()
      .matches(/^[aA-zZ\s]+$/, 'Only alphabets allowed')
      .nullable(false)
      .required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    country: Yup.string().required('Required'),
    phoneNumber: Yup.string()
      .transform(value => value.trim())
      .phone(phoneNumberCountryCode, false, 'Invalid phone number')
      .required('Required'),
    interestedVariants: Yup.array().required('Required'),
    optInConfirmation: Yup.boolean().required('Required'),
  });

  const handleVariantChange = event => {
    if (event.target.checked) {
      if (!interestedVariants.includes(event.target.value)) {
        setInterestedVariants(prevState => [...prevState, event.target.value]);
      }
    } else {
      setInterestedVariants(prevState =>
        prevState.filter(item => item !== event.target.value)
      );
    }
  };

  const trackAnalytics = () => {
    trackProcess('lead-collection', 'complete');
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={LeadCollectionSchema}
        enableReinitialize={true}
        onSubmit={async values => {
          setIsFormSubmitted(true);
          trackAnalytics();
          // eslint-disable-next-line no-console
          console.log(values);
          const response = await createSalesLead(
            firstName,
            lastName,
            email,
            phoneNumber,
            campaignId,
            country,
            interestedVariants,
            optInConfirmation
          ).then(response => {
            setLeadCollectionResponse(response);
            setIsFormSubmitted(false);
            setIsComplete(true);
          });
        }}
      >
        {({
          errors,
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          touched,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit} style={{ paddingBottom: '30px' }}>
            <div style={{ padding: '20px 0px' }}>
              <FormLabel className={styles.formLabel}>
                {l(content.selectVariantTitle, language)}
              </FormLabel>
              <div style={{ paddingTop: '10px' }}>
                <VariantImage />
                <FormGroup>
                  {CarVariants &&
                    CarVariants.map(function (carVariant) {
                      return (
                        <Field
                          type="checkbox"
                          component={VariantCheckbox}
                          name="variants"
                          id={carVariant.value}
                          key={carVariant.value}
                          value={carVariant.value}
                          label={carVariant.label}
                          onChange={e => {
                            handleVariantChange(e);
                            handleChange(e);
                          }}
                        />
                      );
                    })}
                </FormGroup>
              </div>
            </div>

            <Field
              label={'Campaign'}
              name={'campaign'}
              value={values.campaignId}
              type={'hidden'}
            />
            <Typography className={styles.title}>
              {l(content.personalInfoLabel, language)}
            </Typography>
            <Field
              label="First Name"
              name="firstName"
              value={values.firstName}
              required={true}
              onChange={e => {
                setFirstName(e.target.value);
                handleChange(e);
              }}
              onBlur={handleBlur}
              style={{ flexDirection: 'row' }}
              touched={touched.firstName}
              errors={errors}
              component={Input}
            />
            <Field
              label="Last Name"
              name="lastName"
              value={values.lastName}
              required={true}
              onChange={e => {
                setLastName(e.target.value);
                handleChange(e);
              }}
              onBlur={handleBlur}
              touched={touched.lastName}
              errors={errors}
              component={Input}
            />
            <Field
              label="Email"
              name="email"
              value={values.email}
              required={true}
              onChange={e => {
                setEmail(e.target.value);
                handleChange(e);
              }}
              onBlur={handleBlur}
              touched={touched.email}
              errors={errors}
              component={Input}
            />
            <Field
              id={'country'}
              label={'Country'}
              name={'country'}
              value={values.country}
              required={true}
              onChange={e => {
                setPhoneNumberCountryCode(e.target.value);
                setCountry(e.target.value);
                handleChange(e);
              }}
              onBlur={handleBlur}
              touched={touched.country}
              errors={errors}
              component={CountrySelect}
            />
            <Field
              label="Phone number"
              name="phoneNumber"
              value={values.phoneNumber}
              required={true}
              onChange={(value, country) => {
                setPhoneNumber(value);
                if (country['countryCode'] !== phoneNumberCountryCode) {
                  setPhoneNumberCountryCode(country['countryCode']);
                  setFieldValue('phoneNumber', `+${country['dialCode']}`, true);
                } else {
                  setPhoneNumberCountryCode(country['countryCode']);
                  setFieldValue('phoneNumber', value, true);
                }
              }}
              onBlur={handleBlur}
              defaultCountry={phoneNumberCountryCode}
              touched={touched.phoneNumber}
              errors={errors}
              dark={false}
              component={PhoneNumberInput}
            />
            <FormGroup>
              <Field
                type="checkbox"
                component={VariantCheckbox}
                name="optInConfirmation"
                id={'optInConfirmation1'}
                key={'optInConfirmation1'}
                value={values.optInConfirmation}
                showBorder={false}
                labelStyle={{
                  fontSize: '17px',
                  fontWeight: '400',
                  padding: 0,
                  lineHeight: '24px ',
                }}
                label={l(content.consentText, language)}
                onChange={e => {
                  setOptInConfirmation(!!e.target.checked);
                  handleChange(e);
                }}
              />
            </FormGroup>

            <Button
              text={'Share'}
              type={'submit'}
              height="56px"
              dark={false}
              disabled={isFormSubmitted}
              style={{ width: isMobile ? '100%' : '50%' }}
            />
            <Terms language={language} reserve={false} />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default LeadInputForm;
